/**
 * Lobby screen — Screen 1 of the two-screen client.
 *
 * Collects the two pieces of information needed to open a connection:
 * the channel secret and the user's display name. The WebSocket URL is
 * **not** part of the form — it is derived from the page origin in
 * `main.ts` (production: `${origin}/ws`; dev: same path, proxied to the
 * local rust server by Vite).
 *
 * The secret is *never* persisted (entered each session by deliberate
 * design — URLs containing secrets are unsafe and so is browser storage
 * of channel passwords). The display name is persisted in `localStorage`
 * so returning users do not have to type it.
 */

/** Inputs the user supplied. Passed to the lobby's `onConnect` callback. */
export interface LobbyConnectRequest {
    secret: string;
    displayName: string;
}

/** Constructor options for [`renderLobby`]. */
export interface LobbyOptions {
    /** Container the form is appended into. */
    container: HTMLElement;
    /** Called when the user submits a valid form. */
    onConnect: (req: LobbyConnectRequest) => void;
    /** Default display name to seed the input (e.g. from localStorage). */
    defaultDisplayName?: string;
    /**
     * Optional error message to show above the submit button — used
     * when the previous connection attempt failed and the user has been
     * returned to the lobby.
     */
    initialError?: string;
}

/** Handle returned by [`renderLobby`] for teardown. */
export interface LobbyHandle {
    /** Show an error message above the submit button. */
    setError(message: string | null): void;
    /** Enable / disable the submit button (e.g. while connecting). */
    setBusy(busy: boolean): void;
    /** Detach the form from the DOM. */
    destroy(): void;
}

/**
 * Render the lobby into `container` and return a handle.
 *
 * The form validates only that both fields are non-empty — anything
 * beyond that is left to the transport layer to discover and surface as
 * an error.
 */
export function renderLobby(opts: LobbyOptions): LobbyHandle {
    const root = document.createElement("form");
    root.className = "lobby";
    root.noValidate = true;
    opts.container.appendChild(root);

    const heading = document.createElement("h1");
    heading.textContent = "nettacco";
    root.appendChild(heading);

    const secretField = makeInput({
        label: "Secret",
        type: "password",
        name: "secret",
        placeholder: "Channel secret",
        value: "",
        autocomplete: "current-password",
        required: true,
    });
    const nameField = makeInput({
        label: "Display name",
        type: "text",
        name: "displayName",
        // The browser would also auto-suggest, but we already restore the
        // last-used name from localStorage so a second source of suggestions
        // would just duplicate it. Disabled for tidiness.
        placeholder: "Your name",
        value: opts.defaultDisplayName ?? "",
        autocomplete: "off",
        required: true,
    });
    root.appendChild(secretField.label);
    root.appendChild(nameField.label);

    const errorEl = document.createElement("div");
    errorEl.className = "error";
    errorEl.setAttribute("role", "alert");
    errorEl.textContent = opts.initialError ?? "";
    root.appendChild(errorEl);

    const submit = document.createElement("button");
    submit.type = "submit";
    submit.textContent = "Connect";
    root.appendChild(submit);

    const onSubmit = (ev: SubmitEvent): void => {
        ev.preventDefault();
        const secret = secretField.input.value;
        const displayName = nameField.input.value.trim();

        if (secret === "" || displayName === "") {
            setError("All fields are required.");
            return;
        }
        setError(null);
        opts.onConnect({ secret, displayName });
    };
    root.addEventListener("submit", onSubmit);

    const setError = (message: string | null): void => {
        errorEl.textContent = message ?? "";
    };

    return {
        setError,
        setBusy(busy: boolean): void {
            submit.disabled = busy;
            submit.textContent = busy ? "Connecting…" : "Connect";
        },
        destroy(): void {
            root.removeEventListener("submit", onSubmit);
            root.remove();
        },
    };
}

interface InputSpec {
    label: string;
    type: string;
    name: string;
    placeholder: string;
    value: string;
    autocomplete: AutoFill;
    required: boolean;
}

function makeInput(spec: InputSpec): { label: HTMLLabelElement; input: HTMLInputElement } {
    const label = document.createElement("label");
    const text = document.createElement("span");
    text.textContent = spec.label;
    const input = document.createElement("input");
    input.type = spec.type;
    input.name = spec.name;
    input.placeholder = spec.placeholder;
    input.value = spec.value;
    input.autocomplete = spec.autocomplete;
    input.required = spec.required;
    label.appendChild(text);
    label.appendChild(input);
    return { label, input };
}
